import { cn } from 'helpers/cn';
import { correctionEffect } from 'helpers/statistics-glossary';
import { Select } from '@/components/Select';
import { TooltipProvider } from '@/components/ui/tooltip';
import { MetricLabel } from './MetricLabel';
import { CORRECTION_METHOD_OPTIONS, EVALUATION_METHOD_OPTIONS } from './control-options';

/**
 * Controls that only affect the significance read-out, placed with it rather
 * than with the objective and currency selectors above the results table,
 * those change what is being measured, these change how it is judged.
 */
type EvaluationControlsProps = {
  manager: ReturnType<typeof import('@/state/experiments/useExperimentStats').useExperimentStats>;
};

// Kept to roughly two lines at the constrained width: the per-variant readings
// under the significance table now carry the plain-language explanation, so this
// only has to say what the method is.
const METHOD_DESCRIPTION: Record<string, string> = {
  frequentist:
    'How unlikely this result would be if the variant made no difference, reported as a p-value against the control.',
  bayesian: 'The probability that each variant is the best one, with the loss expected from choosing it if it is not.',
};

export const EvaluationControls = ({ manager }: EvaluationControlsProps) => {
  const { evaluationMethod, setEvaluationMethod, correctionMethod, setCorrectionMethod, significance } = manager;
  const isFrequentist = evaluationMethod === 'frequentist';

  // How many variants the correction is actually adjusting across. With one, every
  // method reduces to alpha and switching them cannot change anything, which is the
  // state most experiments are in, and previously looked like a broken control.
  const comparisons = significance?.length ?? 0;
  const correction = correctionEffect(correctionMethod, comparisons);

  return (
    // Own surface with an accent edge: these two choices change how every number
    // in the significance table is judged, so they should not read as incidental
    // dropdowns between two cards.
    <TooltipProvider>
      <div className="bg-panel2 border-accent-line rounded-card border border-l-2" data-tour="results-method">
        {/*
        Same two-column rhythm as the Experiment results header above: a text
        column at a readable measure on the left, controls anchored right. Putting
        the selects directly beside the heading instead left a ragged top edge
        (a 20px heading bottom-aligned against a 12px label) and stranded most of
        the card's width empty.
      */}
        <div className="flex flex-wrap items-start justify-between gap-x-10 gap-y-5 px-4 py-4">
          {/*
          The text takes the room between the heading and the controls, and the two
          paragraphs stack rather than sitting side by side. Put in columns, the
          correction note ended up far to the right, detached from the description it
          qualifies.
        */}
          <div className="min-w-0 flex-1 space-y-2">
            <h3 className="text-foreground text-title font-medium tracking-[-0.01em]">How results are judged</h3>

            <div className="space-y-2">
              <p className="supporting">
                {METHOD_DESCRIPTION[evaluationMethod] ?? METHOD_DESCRIPTION.frequentist}
                {isFrequentist &&
                  ' Testing several variants at once raises the false-positive rate; the correction offsets it.'}
              </p>

              {/*
              The correction's effect stated numerically. Offering four options that are
              mathematically identical for a single-variant experiment, with nothing
              saying so, reads as a control that does not work.
            */}
              {isFrequentist && <p className={cn('supporting', correction.inert && 'text-warn')}>{correction.note}</p>}
            </div>
          </div>

          {/*
          No inner container: wrapping these in their own bordered panel boxed a
          box inside the card and, because of the extra padding, pushed the selects
          out of alignment with the Objective/Currency column directly above.
          Sitting at the card's own padding edge lines the two rows up.
        */}
          <div className="flex shrink-0 flex-wrap items-end gap-3">
            {/* SelectTrigger is w-full, so explicit widths keep these on one row. */}
            <label className="space-y-1.5">
              <MetricLabel metric="evaluationMethod" className="micro-label">
                Method
              </MetricLabel>
              <Select
                options={EVALUATION_METHOD_OPTIONS}
                value={evaluationMethod}
                onValueChange={(value) => setEvaluationMethod(value as typeof evaluationMethod)}
                placeholder="Evaluation method"
                className="border-accent-line bg-field h-9 w-40 rounded-lg"
              />
            </label>

            {isFrequentist && (
              <label className="space-y-1.5">
                <MetricLabel metric="correctionMethod" className="micro-label">
                  Correction
                </MetricLabel>
                <Select
                  options={CORRECTION_METHOD_OPTIONS}
                  value={correctionMethod}
                  onValueChange={(value) => setCorrectionMethod(value as typeof correctionMethod)}
                  placeholder="Correction method"
                  className="border-accent-line bg-field h-9 w-52 rounded-lg"
                />
              </label>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};
